// Uniform resolution for Studio's *generic* Easy Read evaluation leaflet.
// The published assignments are obtained from the same /api/generic-uniforms
// endpoint as Generic NHS management; offline fallbacks come directly from the
// patient app runtime. No Trust-specific or draft uniform assignment is used.
import { getGenericUniformOverrides } from '../../../src/lib/genericUniformRuntime.js';
import { roles as canonicalRoles } from './library.js';
import { colours } from './trustLibrary.js';

const canonicalRoleByReference = new Map(
  canonicalRoles.flatMap((role) => [role.roleId, role.roleKey].filter(Boolean).map((reference) => [reference, role])),
);
const uniformColours = new Map(colours.map((colour) => [colour.id, colour]));

export function resolveEasyReadPrintUniform(role, publishedOverrides = null) {
  const canonical = canonicalRoleByReference.get(role?.id)
    || canonicalRoleByReference.get(role?.roleKey)
    || canonicalRoleByReference.get(role?.role_key);
  const roleKey = canonical?.roleKey || role?.roleKey || role?.role_key || role?.id || null;

  // Paramedic's centrally fixed patient illustration always takes precedence.
  if (roleKey === 'paramedic') return { kind: 'paramedic', colourId: 'paramedic_green', source: 'fixed' };

  // An explicitly saved Generic NHS role configuration takes precedence over
  // the same bundled defaults that the patient app uses without network.
  const published = roleKey && publishedOverrides && Object.prototype.hasOwnProperty.call(publishedOverrides, roleKey)
    ? publishedOverrides[roleKey]
    : null;
  const bundled = roleKey ? getGenericUniformOverrides()[roleKey] : null;
  const assignment = published || bundled;
  const colourId = assignment?.colourId || canonical?.presentation?.defaultUniformId || null;
  const garmentId = assignment?.garmentId || (colourId === 'no_uniform' ? 'no_uniform' : 'scrubs');
  const source = published ? 'published' : bundled ? 'bundled' : 'canonical';

  if (!colourId) return { kind: 'unknown', colourId: null, source };
  if (colourId === 'no_uniform' || garmentId === 'no_uniform') {
    return { kind: 'professional', colourId: 'no_uniform', source };
  }
  const colour = uniformColours.get(colourId);
  if (!colour || colourId === 'no_uniform') return { kind: 'unknown', colourId, source };

  // Keep historical `tunic` compatible with the published Generic NHS editor
  // and patient app: only `healthcare_tunic` selects the healthcare-tunic SVG.
  return {
    kind: 'garment',
    colourId,
    garmentId,
    templateId: garmentId === 'healthcare_tunic' ? 'healthcare-tunic-v1' : 'scrub-vneck-v1',
    body: colour.body,
    trim: colour.trim,
    source,
  };
}


export function getPlainUniformDescription(uniform) {
  if (!uniform || uniform.kind === 'unknown') {
    return 'Staff clothes may vary. Ask your hospital team if you are unsure.';
  }
  if (uniform.kind === 'professional') {
    return 'Usually wears professional clothes rather than a standard uniform.';
  }
  if (uniform.kind === 'paramedic') {
    return 'Usually wears green emergency-service clothing.';
  }
  const colour = uniformColours.get(uniform.colourId);
  const rawName = colour?.name || '';
  // Colour labels can historically include the garment name (for example,
  // "Green Scrubs"). The garment is appended separately, so remove it here
  // to avoid output such as "green scrubs scrubs".
  const plainColour = rawName
    .replace(/\b(?:scrubs?|tunic|uniform)\b/gi, '')
    .replace(/\s+with\s+([^,]+?)\s+trim\b/gi, ' and $1')
    .replace(/\btrim\b/gi, 'edge')
    .replace(/\s{2,}/g, ' ')
    .trim()
    .toLowerCase();
  const garment = uniform.garmentId === 'healthcare_tunic' || uniform.templateId === 'healthcare-tunic-v1'
    ? 'a tunic'
    : 'scrubs';
  return plainColour
    ? `Usually wears ${plainColour} ${garment}.`
    : `Usually wears ${garment}.`;
}
